// Pure helpers for the watches wizard's click-to-build selector. The wizard
// renders a preview sample as a clickable JSON tree; clicking a node writes
// that node's selector-grammar path (dot names, `[N]` indices — see
// packages/types/src/watch-selector.ts) into the selector input. The `$` root
// maps to the EMPTY selector ("watch the whole response"), so paths built
// here are bare (`drones[0].status`), which `parseWatchSelector` accepts.

/**
 * True when `key` can appear in a selector path. The grammar only allows
 * `[A-Za-z0-9_-]+` names — object keys with dots, spaces, etc. cannot be
 * expressed, so the tree renders them unclickable rather than emit garbage.
 */
export function isSelectableKey(key: string | number): boolean {
  return typeof key === 'number' || /^[A-Za-z0-9_-]+$/.test(key);
}

/**
 * Append one step to a selector path. `parent` is either a bare selector
 * path, `''`, or `'$'` (both mean root). Numbers become `[N]` accesses
 * (legal even at the root: `[0].id`); strings become dot-joined names.
 */
export function childPath(parent: string, key: string | number): string {
  const trimmed = parent.trim();
  const base = trimmed === '$' ? '' : trimmed;
  if (typeof key === 'number') return `${base}[${key}]`;
  return base === '' ? key : `${base}.${key}`;
}

/**
 * Replace every concrete array index with the `[*]` map so a selector built
 * by clicking one element generalizes to all elements
 * (`drones[3].status` → `drones[*].status`).
 */
export function generalizeIndices(selector: string): string {
  return selector.replace(/\[\d+\]/g, '[*]');
}

/**
 * Canonicalize a path for churn comparison: preview `churnedPaths` arrive
 * `$`-rooted (`$.a.b[0]` — see `diffWatchPaths`), tree paths are bare.
 */
function rooted(path: string): string {
  const t = path.trim();
  if (t === '' || t === '$') return '$';
  if (t.startsWith('$.') || t.startsWith('$[')) return t;
  if (t.startsWith('[')) return `$${t}`;
  return `$.${t}`;
}

/**
 * True when `path` (bare or `$`-rooted) is itself churned or lies under a
 * churned ancestor — `$.a` churned makes `$.a.b` and `$.a[0]` churned. The
 * `.`/`[` boundary check keeps `$.ab` from matching a churned `$.a`.
 */
export function isChurned(path: string, churnedPaths: string[]): boolean {
  const p = rooted(path);
  for (const raw of churnedPaths) {
    const c = rooted(raw);
    if (p === c || p.startsWith(`${c}.`) || p.startsWith(`${c}[`)) return true;
  }
  return false;
}
